import { Box, Input, InputProps, useAlgoliaSearch } from "@govlaunch/web";
import Downshift, { DownshiftProps } from "downshift";
import React, { ChangeEvent } from "react";
import { useIntl } from "react-intl";

interface ITagAlgoliaHit {
  _id: string;
  name: string;
  slug: string;
  objectID: string;
}

interface ITagItem {
  value: string;
  name: string;
}

interface ITagsFilterSelectProps extends Omit<DownshiftProps<ITagItem[]>, "selectedItem" | "onChange"> {
  selectedItems: ITagItem[];
  onChange: (tags: ITagItem[]) => void;
  disabled?: boolean;
  tagTypes: ("STORY" | "PRODUCT" | "PROJECT" | "AWARD")[];
}

export default function TagsFilterSelect({
  tagTypes,
  onChange,
  disabled = false,
  selectedItems,
  ...props
}: ITagsFilterSelectProps) {
  const { results, setSearchState } = useAlgoliaSearch<ITagAlgoliaHit>({
    initialSearchState: {
      index: "tags",
      facetFilters: [tagTypes.map((tagType) => `types:${tagType}`)],
    },
  });
  const intl = useIntl();

  function handleSelection(addedItem: any) {
    if (addedItem) {
      onChange([...selectedItems, addedItem]);
    }
  }

  return (
    <Downshift<ITagItem[]>
      id="Filter by tags"
      itemToString={() => ""}
      onChange={handleSelection}
      stateReducer={(_, changes) => {
        switch (changes.type) {
          case Downshift.stateChangeTypes.controlledPropUpdatedSelectedItem: {
            return {
              ...changes,
              inputValue: "",
            };
          }
          default:
            return changes;
        }
      }}
      selectedItem={selectedItems}
      {...props}
    >
      {({ getInputProps, getMenuProps, getItemProps, highlightedIndex, isOpen }) => (
        <div aria-label="Filter by tags" aria-labelledby={undefined}>
          <Input
            placeholder={intl.formatMessage({
              defaultMessage: "Filter by tags",
              id: "r9kS6n",
            })}
            disabled={disabled}
            {...getInputProps<InputProps>({
              size: "sm",
              "aria-label": "Filter by tags",
              "aria-labelledby": undefined,
              onChange: (e: ChangeEvent<HTMLInputElement>) => {
                const target = e.currentTarget;

                setSearchState((currentState) => ({
                  ...currentState,
                  query: target.value,
                }));
              },
            })}
          />

          {isOpen && (
            <div css={{ position: "relative" }}>
              <Box
                position="absolute"
                width="100%"
                top={2}
                left={0}
                {...getMenuProps({
                  "aria-label": "Filter by Tags",
                  "aria-labelledby": undefined,
                })}
                as="div"
                zIndex="dropdown"
                boxShadow="lg"
                border="1px solid"
                borderColor="gray.200"
                bg="white"
                listStyleType="none"
                maxHeight={300}
                overflowY="auto"
                borderRadius="sm"
              >
                {(results?.hits || [])
                  .filter((hit) => !selectedItems.find((tag) => hit.slug === tag.value))
                  .map((hit, index) => {
                    return (
                      <Box
                        key={hit.slug}
                        as="li"
                        {...getItemProps({
                          item: {
                            name: hit.name,
                            value: hit.slug,
                          } as any,
                        })}
                        color={highlightedIndex === index ? "blue.600" : "gray.900"}
                        px={4}
                        pl={6}
                        py={2}
                        fontSize="sm"
                        _hover={{
                          color: "blue.600",
                        }}
                        cursor="pointer"
                      >
                        {hit.name}
                      </Box>
                    );
                  })}
              </Box>
            </div>
          )}
        </div>
      )}
    </Downshift>
  );
}
